import React, { useState, useEffect } from 'react';
import { FormGroup, FormControl, Table } from 'react-bootstrap';
import moment from 'moment';
import LoaderButton from '../components/LoaderButton';
import { UploadPars, GetOpsLocations, OrderMeta } from '../libs/GraphQL';
import AlertBox from '../components/atoms/AlertBox';
import { uploadFileToS3 } from '../libs/uploadToS3';
import styledComponent from '../utils/styledComponent';
import Loader from '../components/atoms/Loader';

const StyledLoader = styledComponent('section', {
  marginTop: '16px',
  width: '100%',
  '& p': {
    fontSize: '16px',
    margin: '0',
    '& b': {
      fontSize: '20px',
    },
  },
  '& .loading': {
    width: '100%',
    height: '26px',
    background: 'rgba(0, 0, 0, 0.18);',
    marginBottom: '8px',
    position: 'relative',
    textAlign: 'center',
    fontWeight: 'bold',
    lineHeight: '24px',
    borderRadius: '12px',
    overflow: 'hidden',

    '& .bar': {
      width: '50%',
      height: '100%',
      background: '#368007',
      position: 'absolute',
      borderRadius: '12px',
      top: 0,
      left: 0,
      transition: 'width 0.4s ease-in-out',
    },

    '& .barText': {
      position: 'absolute',
      width: '100%',
      textAlign: 'center',
      color: '#ffffff',
      textShadow: '1px 1px 1px rgba(0, 0, 0, 0.20)',
    },
  },
});

const UploadParsV2 = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState('');
  const [fileContents, setFileContents] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const [locations, setLocations] = useState([]);
  const [salesforceFields, setSalesforceFields] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [curOrders, setCurOrders] = useState({});

  const [completedFiles, setCompletedFiles] = useState(0);
  const progressPercent = Math.round((completedFiles / 16) * 100) || 0;

  const BATCH_SIZE = 500;

  const getSalesforceFields = async () => {
    try {
      const locationIds = {};
      const tempSalesforceFields = {};
      let nextToken = null;

      do {
        const rawLocations = await GetOpsLocations(nextToken, ['LIVE']);
        if (rawLocations?.data?.opsLocations) {
          const locations = rawLocations.data.opsLocations.locations || [];
          for (const location of locations) {
            locationIds[location.id] = location.type;
            tempSalesforceFields[location.id] = {
              fulfillmentCenter: location.fulfillmentCenter || undefined,
              routingTag: location.routingTag || undefined,
            };
            if (location.type === 'FRIDGE') {
              tempSalesforceFields[location.id] = {
                ...tempSalesforceFields[location.id],
                parkToFridgeInstructions:
                  location.parkToFridgeInstructions || undefined,
              };
            }
          }
          nextToken = rawLocations.data.opsLocations.nextToken;
        }
      } while (nextToken);

      setLocations(locationIds);
      setSalesforceFields(tempSalesforceFields);

      setCompletedFiles((prev) => prev + 1);
    } catch (e) {
      console.error(e);
    }
  };

  // Fetch existing Orders
  const fetchOrdersForNextDays = async (startDay) => {
    try {
      const allOrdersData = {};
      const promises = [];

      for (let i = 0; i <= 14; i++) {
        const date = moment(startDay).add(i, 'days').format('YYYY-MM-DD');

        const p = OrderMeta(null, null, date)
          .then((dataObj) => {
            const data = dataObj.data?.ordersByDay?.orders;
            if (data && data.length > 0) {
              allOrdersData[date] = data;
            }
          })
          .finally(() => {
            setCompletedFiles((prev) => prev + 1);
          });

        promises.push(p);
      }

      await Promise.all(promises);
      setCurOrders(allOrdersData);
    } catch (e) {
      console.error('Error loading Orders data:', e);
    }
  };

  const validateLine = () => {
    const errorsList = [];

    fileContents.fileContents.forEach((line) => {
      const stockingId = `${line.dropOffDate}#${line.location}#${line.stockingIndex}`;
      const errorTitle = `${stockingId} - has an error:`;
      if (
        !(typeof line.overCapacityAmount === 'number') ||
        parseInt(line.overCapacityAmount) < 0
      ) {
        errorsList.push({
          title: errorTitle,
          message: `Over Capacity Amount issue with ${line.item}. Accepted values any number 0 or greater.`,
        });
      }
      if (
        line.jit &&
        !(String(line.jit) === 'true' || String(line.jit) === 'false')
      ) {
        errorsList.push({
          title: errorTitle,
          message: `JIT cannot be applied for ${line.item}. Accepted values are true or false. Please revise the format and resubmit.`,
        });
      }

      if (
        line.priorityBoost &&
        !(
          String(line.priorityBoost) === 'true' ||
          String(line.priorityBoost) === 'false' ||
          line.priorityBoost === ''
        )
      ) {
        errorsList.push({
          title: errorTitle,
          message: `"priorityBoost" cannot be applied for ${line.item}. Accepted values are true or false. Please revise the format and resubmit.`,
        });
      }

      if (line.jit && line.deliveryDate !== line.dropOffDate) {
        errorsList.push({
          title: errorTitle,
          message: `JIT cannot be applied for ${line.item}. Delivery Date must equal Drop Off Date.`,
        });
      }

      if (salesforceFields[line.location]?.routingTag === undefined) {
        errorsList.push({
          title: errorTitle,
          message: `Error with Routing Tag for ${line.location}`,
        });
      }
      if (salesforceFields[line.location]?.fulfillmentCenter === undefined) {
        errorsList.push({
          title: errorTitle,
          message: `Error with Territory Tag for ${line.location}`,
        });
      }
      if (locations[line.location] === 'FRIDGE') {
        if (
          salesforceFields[line.location]?.parkToFridgeInstructions ===
          undefined
        ) {
          errorsList.push({
            title: errorTitle,
            message: `Error with Park To Fridge Instructions Tag for ${line.location}`,
          });
        }
      }
    });
    setErrors(errorsList);
    return errorsList.length === 0;
  };

  const createUniqueKey = (line) => {
    return `${line.dropOffDate}#${line.location}#${line.stockingIndex}`;
  };

  // Validate if Orders already exist
  const validateOrderExistence = () => {
    const invalidItems = [];
    const uniqueKeys = new Set();
    if (!curOrders || !fileContents.fileContents) return true;

    fileContents.fileContents.forEach((line) => {
      const key = createUniqueKey(line);
      if (!uniqueKeys.has(key)) {
        uniqueKeys.add(key);

        const allDates = Object.keys(curOrders);
        let found = false;

        for (const dateKey of allDates) {
          const ordersForDate = curOrders[dateKey] || [];
          const hasOrder = ordersForDate.some((order) => {
            const orderStockingIndex = order.stockingIndex
              ? order.stockingIndex
              : 1;
            return (
              order.locationId === line.location &&
              order.dropOffDate === line.dropOffDate &&
              orderStockingIndex === parseInt(line.stockingIndex)
            );
          });

          if (hasOrder) {
            found = true;
            break;
          }
        }

        if (found) {
          invalidItems.push(key);
        }
      }
    });

    if (invalidItems.length > 0) {
      setErrors((prevErrors) => [
        ...prevErrors,
        ...invalidItems.map((itemKey) => ({
          title: itemKey,
          message:
            'There is already a fridge order for this dropOffDate, location, and stockingIndex.',
        })),
      ]);
      return false;
    }
    return true;
  };

  useEffect(() => {
    const today = moment().format('YYYY-MM-DD');
    setLoadingData(true);

    const loadInitialData = async () => {
      await getSalesforceFields();
      setCompletedFiles((prev) => prev + 1);

      await fetchOrdersForNextDays(today);

      setLoadingData(false);
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (fileContents.fileContents) {
      if (validateLine()) {
        const ordersValid = validateOrderExistence();

        if (ordersValid) {
          setShowTable(true);
        } else {
          setShowErrors(true);
          setFile('');
          setFileContents('');
        }
      } else {
        setShowErrors(true);
        setFile('');
        setFileContents('');
      }
      setIsLoading(false);
    }
  }, [fileContents]);

  const handleChange = async (event) => {
    setIsLoading(true);
    event.persist();
    setFile('');
    setFileContents('');
    setErrors([]);
    setShowErrors(false);
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) {
      setIsLoading(false);
      return;
    }
    window.Papa.parse(uploadedFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete(results) {
        if (results?.errors?.length > 0) {
          console.error('Error parsing CSV:', results.errors[0]);
          setFile('');
          setFileContents('');
          const s = `Issue uploading CSV. Please try again. \nError: ${results.errors[0].message}`;
          alert(s);
          setIsLoading(false);
          return;
        }
        setFileContents({
          fileContents: results.data,
        });
        setFile(uploadedFile);
      },
    });
  };

  const validateForm = () => {
    return file !== '';
  };

  const packageData = () => {
    // this will be a dictionary that maps uniqueId to
    // the parsAndItems that are at that given location/date/id combo
    const cleanData = {};
    const arr = [];
    fileContents.fileContents.forEach((parItemObj) => {
      const uniqueId = `${parItemObj.location}#${parItemObj.dropOffDate}#${parItemObj.stockingIndex}`;
      if (!(uniqueId in cleanData)) {
        const tempObj = {
          location: parItemObj.location,
          deliveryDate: parItemObj.deliveryDate,
          dropOffDate: parItemObj.dropOffDate,
          parsAndItems: [],
          jit: parItemObj.jit,
          stockingIndex: parItemObj.stockingIndex,
          overCapacityAmountsAndItems: [],
          priorityBoostItems: [],
        };
        cleanData[uniqueId] = tempObj;
        arr.push(tempObj);
      }
      // the uniqueId already has a mapping so only
      // add new parsAndItemsObj
      cleanData[uniqueId].parsAndItems.push({
        pars: parItemObj.par,
        itemName: parItemObj.item,
      });
      cleanData[uniqueId].overCapacityAmountsAndItems.push({
        overCapacityAmount: parItemObj.overCapacityAmount,
        itemName: parItemObj.item,
      });
      if (parItemObj.priorityBoost === true) {
        cleanData[uniqueId].priorityBoostItems.push(parItemObj.item);
      }
    });
    return arr;
  };

  const uploadParsFunc = async () => {
    setIsLoading(true);
    let error = false;
    let errorMsg = '';
    const data = packageData();
    const payload = [];
    let subPayload = [];
    let count = 0;

    data.forEach((stockindId) => {
      if (count < BATCH_SIZE) {
        subPayload.push(stockindId);
        count += 1;
      }
      if (subPayload.length === BATCH_SIZE) {
        const temp = subPayload;
        payload.push(temp);
        subPayload = [];
        count = 0;
      }
    });
    if (subPayload.length > 0) {
      payload.push(subPayload);
    }

    try {
      await Promise.all(
        payload.map((parsUpload) => {
          return UploadPars(parsUpload).then((res) => {
            if (!res) {
              error = true;
              alert(`Issue uploading CSV. Please try again.`);
            }
          });
        }),
      );
    } catch (e) {
      error = true;
      errorMsg = e?.errors[0]?.message || '';
    }
    setShowTable(false);
    setIsLoading(false);
    setFile('');
    setFileContents('');
    if (!error) {
      const uploadedFile = file;
      try {
        const uploadResult = await uploadFileToS3(
          uploadedFile,
          'pars-mw-allocation-upload',
        );
      } catch (uploadError) {
        console.warn('Error uploading file to S3:', uploadError);
      }
      alert('Upload Complete');
    } else {
      alert(`Something has failed. Pars were not uploaded. ${errorMsg}`);
    }
  };

  return (
    <div className="Locations">
      {loadingData ? (
        <>
          <Loader />
          <StyledLoader>
            <div className="loading">
              <div className="bar" style={{ width: `${progressPercent}%` }} />
              <div className="barText">{progressPercent}%</div>
            </div>
            <p>
              Initial Data Loading: <b>{`${completedFiles} / 16`}</b>
            </p>
          </StyledLoader>
        </>
      ) : (
        <>
          {fileContents === '' ? (
            <FormGroup controlId="file">
              <FormControl
                onClick={(event) => {
                  event.target.value = null;
                }}
                onChange={(event) => handleChange(event)}
                type="file"
              />
            </FormGroup>
          ) : (
            <LoaderButton
              block
              bsStyle="primary"
              bsSize="large"
              disabled={!validateForm() || showErrors}
              onClick={() => {
                setShowTable(false);
                setFile('');
                setFileContents('');
              }}
              style={{ marginBottom: '16px' }}
              type="button"
              text="Choose another file"
            />
          )}

          {showErrors && errors.length > 0 && (
            <AlertBox title={`Validation Errors (${errors.length})`}>
              <ul>
                {errors.map((error) => {
                  const errorKey = `${error.title}-${error.message}`;
                  return (
                    <li key={errorKey}>
                      <b>{error.title}</b> <br />
                      <span>{error.message}</span>
                    </li>
                  );
                })}
              </ul>
            </AlertBox>
          )}
          {showTable && (
            <Table bordered responsive>
              <thead>
                <tr>
                  {Object.keys(fileContents.fileContents[0]).map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fileContents.fileContents.slice(0, 1000).map((obj) => {
                  const rowKey = `${obj.location}-${obj.dropOffDate}-${obj.item}`;
                  const tds = Object.values(obj).map((val, i) => (
                    <td key={i}>{String(val)}</td>
                  ));
                  return <tr key={rowKey}>{tds}</tr>;
                })}
              </tbody>
            </Table>
          )}
          <LoaderButton
            block
            bsStyle="primary"
            bsSize="large"
            disabled={!validateForm() || showErrors}
            onClick={uploadParsFunc}
            type="submit"
            isLoading={isLoading}
            text="Upload Pars"
            loadingText="Uploading Pars…"
          />
        </>
      )}
    </div>
  );
};

export default UploadParsV2;
